import {
  Alert,
  Button,
  Flex,
  Form,
  type FormInstance,
  Tag,
  message
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { SmsStatusUnreadable, SmsStoppedBanner } from "./SmsAlertsNotice.tsx"
import { VerifyPhoneModal } from "./VerifyPhoneModal.tsx"
import { useSmsVerification } from "./use-sms-verification.ts"

const nationalDigits = (phone: string | null | undefined) =>
  (phone ?? "").replace(/\D/g, "").slice(-10)

interface SmsAlertsStatusProps {
  form: FormInstance
  savedPhone: string | null | undefined
}

/** SMS proof for the phone on the profile: whether it is verified, how to
 *  verify it, and what a change to it will do. */
export function SmsAlertsStatus({ form, savedPhone }: SmsAlertsStatusProps) {
  const { t } = useTranslation()
  const { state, failed, reload, setState } = useSmsVerification()
  const [verifying, setVerifying] = useState(false)
  const typedPhone = Form.useWatch("phone", form) as string | undefined
  const firstSavedPhone = useRef(savedPhone)

  useEffect(() => {
    if (savedPhone === firstSavedPhone.current) return
    firstSavedPhone.current = savedPhone
    void reload()
  }, [savedPhone, reload])

  if (!state && failed) {
    return (
      <div className="mb-6" data-testid="sms-alerts-status">
        <SmsStatusUnreadable onRetry={() => void reload()} />
      </div>
    )
  }
  if (!state?.available) return null

  const edited =
    typedPhone !== undefined &&
    nationalDigits(typedPhone) !== nationalDigits(state.phone)
  const leavesVerifiedNumber =
    state.verified &&
    nationalDigits(typedPhone) !== nationalDigits(state.verified_phone)

  return (
    <Flex vertical gap={8} className="mb-6" data-testid="sms-alerts-status">
      <Flex align="center" gap={8} wrap>
        {state.verified ? (
          <Tag color="green">{t("smsVerification.verified")}</Tag>
        ) : (
          <Tag>{t("smsVerification.notVerified")}</Tag>
        )}
        {!state.verified && state.phone && !edited && (
          <Button
            size="small"
            type="primary"
            onClick={() => setVerifying(true)}
          >
            {t("smsVerification.verifyButton")}
          </Button>
        )}
      </Flex>
      {typedPhone !== undefined && leavesVerifiedNumber && (
        <Alert
          type="warning"
          showIcon
          message={t("smsVerification.phoneChangeWarning")}
        />
      )}
      {!state.verified && edited && (
        <Alert type="info" showIcon message={t("smsVerification.saveFirst")} />
      )}
      {state.stopped && <SmsStoppedBanner startNumber={state.start_number} />}
      {state.last_failure && (
        <Alert
          type="error"
          showIcon
          message={t("smsVerification.lastFailure", {
            reason: state.last_failure.reason
          })}
        />
      )}
      {state.phone && (
        <VerifyPhoneModal
          open={verifying}
          phone={state.phone}
          startNumber={state.start_number}
          onClose={() => setVerifying(false)}
          onVerified={(verified) => {
            setVerifying(false)
            setState(verified)
            void message.success(t("smsVerification.verifiedToast"))
          }}
        />
      )}
    </Flex>
  )
}
